import { displayToastrAlert } from '../ui/toastr-alert.js';

/*
 * Favorites — the item-page star and the favorites-page remove control.
 *
 * DOM contract (server markup must match these; nothing else is read):
 *
 *   [data-favorite-toggle]   button, needs data-aaia + data-pn.
 *                            aria-pressed is the ONLY state store — the CSS
 *                            picks the filled or outline glyph off it, so the
 *                            rendered state and the announced state cannot
 *                            drift apart.
 *   [data-favorite-remove]   button, needs data-aaia + data-pn. Wins outright
 *                            over data-favorite-toggle if a button somehow
 *                            carries both: two handlers on one click would post
 *                            twice, and the toggle — reading no aria-pressed,
 *                            so assuming "not favorited" — would race to re-add
 *                            the very row the remove is deleting.
 *   [data-favorite-row]      the ancestor of a remove button that gets pulled
 *                            out of the DOM when the row is removed.
 *   [data-favorites-list]    wrapper around the rows.
 *   [data-favorites-empty]   empty state, carries d-none while rows exist.
 *   [data-favorites-count]   any number of nodes whose text is the count.
 *   [data-favorites-sort]    the sort <select>.
 *   .favorites-sort-form     the GET form wrapping it. A class and not :has(),
 *                            which is a slow delegated selector and, since
 *                            jQuery 3.4, one whose engine varies.
 *
 * Announcements: exactly one channel per event, never two. A successful toggle
 * announces through the status region and shows no toast (starring is a hot
 * path and a toast per click is noise). A successful removal and every failure
 * go through the toast, whose container SweetAlert2 already marks aria-live
 * polite — announcing those twice would just stutter.
 */

var TOGGLE_URL = '/favorites/toggle';
var REMOVE_URL = '/favorites/remove';
var STATUS_REGION_ID = 'favorites-status';
var MAX_MESSAGE_LENGTH = 200;

/**
 * Server strings reach the toast as SweetAlert2's `title`, which it renders as
 * HTML. A part number is attacker-influenceable and rides along in these
 * messages, so nothing dynamic gets there unescaped.
 */
function escapeHtml(value) {
    return $('<div>').text(String(value == null ? '' : value)).html();
}

function safeMessage(value, fallback) {
    if (typeof value !== 'string' || value === '') {
        return fallback;
    }
    return escapeHtml(value.substring(0, MAX_MESSAGE_LENGTH));
}

function errorMessage(xhr) {
    if (xhr && (xhr.status === 401 || xhr.status === 403)) {
        return 'Your session has expired. Sign in again to change your favorites.';
    }
    var payload = xhr ? xhr.responseJSON : null;
    return safeMessage(
        payload ? payload.error : null,
        'Could not update your favorites. Try again in a moment.'
    );
}

/**
 * Every async call in this app carries the acting user, so favorites written
 * while AAM staff browse as a customer land on the customer's list rather than
 * on the staff member's.
 */
function withActingUser(url) {
    var $au = $('#au');
    if (!$au.length || !$au.val()) {
        return url;
    }
    return (
        url +
        (url.indexOf('?') !== -1 ? '&' : '?') +
        'au=' +
        encodeURIComponent($au.val())
    );
}

function statusRegion() {
    var $region = $('#' + STATUS_REGION_ID);
    if (!$region.length) {
        $region = $('<div>', {
            id: STATUS_REGION_ID,
            'class': 'visually-hidden',
            role: 'status',
            'aria-live': 'polite',
        }).appendTo('body');
    }
    return $region;
}

function announce(message) {
    var $region = statusRegion();
    // Assistive tech only announces a live region when its text *changes*, so
    // starring two parts in a row would go silent the second time. Clearing
    // first forces the change.
    $region.text('');
    window.setTimeout(function () {
        $region.text(message);
    }, 50);
}

function isPressed($button) {
    return String($button.attr('aria-pressed')) === 'true';
}

function setPressed($button, pressed) {
    $button.attr('aria-pressed', pressed ? 'true' : 'false');
}

/**
 * aria-disabled rather than the disabled property: a disabled button drops out
 * of the tab order mid-interaction, so a keyboard user who just pressed Enter
 * would lose their place. The click handlers check isLocked() instead.
 */
function lock($button) {
    $button.attr('aria-disabled', 'true').addClass('is-busy');
}

function unlock($button) {
    $button.removeAttr('aria-disabled').removeClass('is-busy');
}

function isLocked($button) {
    return $button.attr('aria-disabled') === 'true';
}

function partOf($button) {
    return {
        aaia: String($button.attr('data-aaia') || ''),
        pn: String($button.attr('data-pn') || ''),
    };
}

function updateCount(count) {
    if (count === undefined || count === null) {
        return;
    }
    var parsed = parseInt(count, 10);
    if (isNaN(parsed) || parsed < 0) {
        return;
    }
    // .text(), never .html() — the count is a number here, but this selector is
    // the one place a server value routinely lands in the page.
    $('[data-favorites-count]').text(String(parsed));
}

function isFavoritedResponse(response) {
    return (
        response.favorited === true ||
        response.favorited === 1 ||
        response.favorited === '1'
    );
}

function toggleFavorite($button) {
    if (isLocked($button)) {
        return;
    }

    var part = partOf($button);
    if (!part.aaia || !part.pn) {
        displayToastrAlert(
            'error',
            'This part is missing the information needed to favorite it.',
            true
        );
        return;
    }

    var wasPressed = isPressed($button);
    var wantPressed = !wasPressed;

    // Optimistic: flip now, roll back in the failure paths below.
    setPressed($button, wantPressed);
    lock($button);

    $.ajax({
        url: withActingUser(TOGGLE_URL),
        type: 'post',
        dataType: 'json',
        // Explicit desired state rather than a bare toggle, so a stale initial
        // state or a duplicate post cannot flip the row the wrong way.
        data: { aaia: part.aaia, pn: part.pn, on: wantPressed ? 1 : 0 },
        success: function (response) {
            if (!response || response.ok !== true) {
                setPressed($button, wasPressed);
                displayToastrAlert(
                    'error',
                    safeMessage(
                        response ? response.error : null,
                        'Could not update your favorites. Try again in a moment.'
                    ),
                    true
                );
                return;
            }

            var favorited = isFavoritedResponse(response);
            setPressed($button, favorited);
            updateCount(response.count);
            announce(
                favorited ? 'Added to favorites.' : 'Removed from favorites.'
            );
        },
        error: function (xhr) {
            setPressed($button, wasPressed);
            displayToastrAlert('error', errorMessage(xhr), true);
        },
        complete: function () {
            unlock($button);
        },
    });
}

/**
 * Removing a row destroys the element focus is sitting on, which would dump
 * focus back to <body> and lose a keyboard user's place in the list. Hand it to
 * the next row's control first, then the previous one, then the empty state.
 */
function moveFocusOutOf($row) {
    var $next = $row.nextAll('[data-favorite-row]').first();
    var $target = $next.length
        ? $next.find('[data-favorite-remove]').first()
        : $row.prevAll('[data-favorite-row]').first().find('[data-favorite-remove]').first();

    if ($target.length) {
        $target.trigger('focus');
        return;
    }

    var $empty = $('[data-favorites-empty]').first();
    if ($empty.length) {
        $empty.attr('tabindex', '-1').trigger('focus');
    }
}

function refreshEmptyState(count) {
    var $list = $('[data-favorites-list]').first();
    if (!$list.length) {
        return;
    }
    if ($list.find('[data-favorite-row]').length !== 0) {
        return;
    }

    // This page ran out of rows but the LIST did not: the user removed the last
    // row of a page that is not the only page, so the rest of their favorites are
    // on other pages. Claiming "you have not saved any favorites yet" next to a
    // header count of 24 would be a lie, and the pagination below is now stale
    // too, so re-request the page instead of rewriting the DOM.
    //
    // index() clamps a page number past the end down to the last page
    // (Favorites.php), so the current URL is always safe to re-request - it lands
    // on the new last page rather than 404ing.
    //
    // A missing or unparseable count falls through to the DOM-only behaviour
    // below, which is what this did before.
    var parsed = parseInt(count, 10);
    if (!isNaN(parsed) && parsed > 0) {
        window.location.assign(window.location.href);
        return;
    }

    $list.addClass('d-none');
    $('[data-favorites-empty]').removeClass('d-none');
}

function removeFavorite($button) {
    if (isLocked($button)) {
        return;
    }

    var part = partOf($button);
    if (!part.aaia || !part.pn) {
        displayToastrAlert(
            'error',
            'This row is missing the information needed to remove it.',
            true
        );
        return;
    }

    var $row = $button.closest('[data-favorite-row]');

    lock($button);

    $.ajax({
        url: withActingUser(REMOVE_URL),
        type: 'post',
        dataType: 'json',
        data: { aaia: part.aaia, pn: part.pn },
        success: function (response) {
            if (!response || response.ok !== true) {
                unlock($button);
                displayToastrAlert(
                    'error',
                    safeMessage(
                        response ? response.error : null,
                        'Could not remove this favorite. Try again in a moment.'
                    ),
                    true
                );
                return;
            }

            updateCount(response.count);

            if ($row.length) {
                moveFocusOutOf($row);
                $row.remove();
                refreshEmptyState(response.count);
            } else {
                unlock($button);
            }

            displayToastrAlert('success', 'Removed from favorites.', true);
        },
        error: function (xhr) {
            unlock($button);
            displayToastrAlert('error', errorMessage(xhr), true);
        },
    });
}

/**
 * Where a sort change navigates to.
 *
 * Mutates the CURRENT url rather than rebuilding one from parts, which is what
 * keeps `au` alive. browse.js takes the other approach — split on '?', build
 * fresh params, then re-add au by sniffing for it and reading #au — and that
 * shape means any param not explicitly re-added is dropped. Dropping au ends an
 * AAM staff "view as" session silently, mid-browse.
 *
 * Truncating the path at /favorites is also what resets to page 1: the page
 * number is a path segment (favorites/page/3), not a query param. A pathname
 * with no /favorites in it is left alone rather than guessed at.
 *
 * Exported so the tests can assert the url itself rather than a navigation. What
 * is worth pinning here is which parameters survive, and that is a property of
 * the string.
 */
/**
 * Navigation, behind one indirection because jsdom makes window.location.assign
 * non-configurable - it cannot be spied on, and replacing window.location
 * wholesale to observe a sort change is a heavier fake than the thing it tests.
 * Called by name (favoritesNav.go) so a test's spy is the function that runs.
 */
export const favoritesNav = {
    go(url) {
        window.location.assign(url);
    },
};

export function sortTargetUrl(value) {
    var url = new URL(window.location.href);

    url.pathname = url.pathname.replace(/(\/favorites)(\/.*)?$/i, '$1');

    if (value) {
        url.searchParams.set('sort', value);
    } else {
        url.searchParams.delete('sort');
    }

    return url.toString();
}

export function initFavorites() {
    // Built up front, not at announce time: a live region injected in the same
    // tick as its first message is routinely missed by screen readers.
    if ($('[data-favorite-toggle], [data-favorite-remove]').length) {
        statusRegion();
    }

    // Delegated — the browse shell swaps content in via AJAX without re-running
    // this init, and favorites rows leave the DOM as they are removed.
    $(document).on(
        'click',
        '[data-favorite-toggle]:not([data-favorite-remove])',
        function (e) {
            e.preventDefault();
            toggleFavorite($(this));
        }
    );

    $(document).on('click', '[data-favorite-remove]', function (e) {
        e.preventDefault();
        removeFavorite($(this));
    });

    $(document).on('change', '[data-favorites-sort]', function () {
        favoritesNav.go(sortTargetUrl($(this).val()));
    });

    // The select lives in a real <form method="get" action="favorites"> so that the
    // control still means something with no JS. But a native submit builds the query
    // string from the form's fields alone, so it would drop au — hence this takes over
    // whenever JS is here to do it.
    $(document).on('submit', '.favorites-sort-form', function (e) {
        e.preventDefault();
        favoritesNav.go(sortTargetUrl($(this).find('[data-favorites-sort]').val()));
    });
}
